import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import RegistrationForm from '../../components/RegistrationForm/RegistrationForm';
import { getActivityDetails } from '../../api/activities';
import { resolvePageContext } from '../../utils/fanMemberUtils';
import { getFanMemberBrandTheme } from '../../config/fanMembersConfig';

const RegistrationPage = ({ fixedActivityType, fixedId, fixedFanSlug }) => {
  const params = useParams();
  const pageContext = resolvePageContext({ fixedFanSlug, fixedActivityType, fixedId, params });
  const { activityId: id, activityType, isFanMemberRoute, navigateTo, fanMember } = pageContext;
  const brand = getFanMemberBrandTheme(fanMember);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  // Theme configuration based on activity type
  const isEventType = activityType === 'event';

  useEffect(() => {
    const fetchActivityDetails = async () => {
      try {
        setLoading(true);
        console.log('Fetching activity details with ID:', id, 'Activity Type:', activityType);
        const response = await getActivityDetails(id);
        console.log('Activity details response:', response);
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch activity details');
        }
        
        setEventData(response.data);
        setTimeout(() => setIsVisible(true), 100);
      } catch (err) {
        console.log('Error fetching activity details:', err);
        // Extract more specific error message from the API response
        const errorMessage = err.response?.data?.error || 
                           err.response?.data?.message || 
                           err.message || 
                           'Failed to load activity details';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityDetails();
  }, [id, activityType]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isEventType 
          ? 'bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50'
          : isFanMemberRoute ? brand.registrationBg : 'bg-gradient-to-br from-slate-900 to-slate-800'
      }`}>
        <div className="text-center">
          <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isEventType 
              ? 'bg-gradient-to-r from-orange-400 to-pink-500 text-white' 
              : isFanMemberRoute ? brand.registrationIconBg + ' text-white' : 'bg-purple-900 text-purple-400'
          }`}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <BeatLoader
            color={isEventType ? "#f97316" : isFanMemberRoute ? brand.loader : "#8b5cf6"}
            loading={loading}
            size={12}
            aria-label="Loading Spinner"
          />
          <p className={`mt-4 font-medium ${
            isEventType ? 'text-orange-700' : 'text-slate-300'
          }`}>
            Loading registration...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-8 ${
        isEventType 
          ? 'bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50'
          : isFanMemberRoute ? brand.registrationBg : 'bg-gradient-to-br from-slate-900 to-slate-800'
      }`}>
        <div className={`max-w-md w-full rounded-lg shadow-lg p-8 text-center ${
          isEventType 
            ? 'bg-white border border-red-200'
            : 'bg-slate-800 border border-red-500'
        }`}>
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            isEventType ? 'bg-red-100 text-red-600' : 'bg-red-900 text-red-400'
          }`}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isEventType ? 'text-slate-900' : 'text-white'}`}>
            Unable to Load Registration
          </h3>
          <p className={`text-sm ${isEventType ? 'text-slate-600' : 'text-slate-300'}`}>
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className={`mt-4 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              isEventType 
                ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600'
                : isFanMemberRoute ? brand.registrationButton + ' text-white' : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return null;
  }

  return (
    <div className={`min-h-screen ${
      isEventType ? 'bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50' : isFanMemberRoute ? brand.registrationBg : 'bg-slate-900'
    }`}>
      {/* Hero Section with Background Image */}
      <div className="relative h-64 sm:h-80 xl:h-96 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${eventData.imageUrl})` }}
        />
        <div className={`absolute inset-0 ${
          isEventType 
            ? 'bg-gradient-to-b from-orange-600/70 via-pink-600/70 to-purple-600/70'
            : isFanMemberRoute ? brand.heroOverlay : 'bg-gradient-to-b from-purple-900/80 to-slate-900/80'
        }`} />
        
        {/* Header Content */}
        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <div className="text-center text-white">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              isEventType ? 'bg-gradient-to-r from-orange-400 to-pink-500' : isFanMemberRoute ? brand.registrationIconBg : 'bg-purple-600'
            }`}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-bold mb-10">
              {isEventType ? 'Event Registration' : 'Join Our Community'}
            </h1>
          </div>
        </div>
      </div>

      {/* Registration Form Section */}
      <div className={`relative -mt-20 xl:-mt-24 pb-16 transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-lg shadow-xl overflow-hidden ${
            isEventType ? 'bg-white' : 'bg-slate-800'
          }`}>
            <div className="grid xl:grid-cols-2 gap-0">
              {/* Event Details Sidebar */}
              <div className={`p-8 xl:p-12 ${
                isEventType 
                  ? 'bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 border-r border-orange-200'
                  : isFanMemberRoute ? brand.sidebarBg + ' border-r' : 'bg-gradient-to-br from-purple-900 to-slate-800 border-r border-slate-700'
              }`}>
                <div className="space-y-6 xl:space-y-8">
                  <div>
                    <h2 className={`text-2xl xl:text-3xl font-bold mb-2 ${
                      isEventType ? 'text-slate-900' : 'text-white'
                    }`}>
                      {eventData.title || 'Registration Details'}
                    </h2>
                    <p className={`text-sm xl:text-base ${
                      isEventType ? 'text-slate-600' : 'text-slate-300'
                    }`}>
                      {eventData.description || 'Please fill out the form to complete your registration.'}
                    </p>
                  </div>


                </div>
              </div>

              {/* Registration Form */}
              <div className="p-8 xl:p-12">
                <div className="mb-6 xl:mb-8">
                  <h3 className={`text-xl xl:text-2xl font-semibold mb-2 ${
                    isEventType ? 'text-slate-900' : 'text-white'
                  }`}>
                    Registration Information
                  </h3>
                  <p className={`text-sm xl:text-base ${
                    isEventType ? 'text-slate-600' : 'text-slate-300'
                  }`}>
                    Please provide your details to complete the registration process.
                  </p>
                </div>
                
                <RegistrationForm event={eventData} />
                
                {/* Login Link */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <p className={`text-sm xl:text-base text-center ${
                    isEventType ? 'text-slate-600' : 'text-slate-300'
                  }`}>
                    Already have an account?{' '}
                    <button
                      onClick={() => {
                        if (isFanMemberRoute) {
                          navigate(navigateTo('login'));
                        } else {
                          navigate(`/${activityType}/${id}/login`);
                        }
                      }}
                      className={`font-medium transition-colors ${
                        isEventType 
                          ? 'text-orange-600 hover:text-pink-600'
                          : isFanMemberRoute ? brand.registrationLink : 'text-purple-400 hover:text-purple-300'
                      }`}
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;